import { Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Chip from '@mui/material/Chip';
import { i18n } from 'i18n';
import { Trans } from '@lingui/react';
import { round } from 'utils';
import {
  flowRateMetricToImperial,
  frictionLossMetricToImperial,
  pumpPressureBarToGravityMetricHead,
  pumpPressureBarToKpa,
  pumpPressureMetricToImperial
} from 'utils/unitConversions';
import useDisplayUnits from 'hooks/useDisplayUnits';
import {
  additionalUnitLabels,
  BasicDisplayUnits,
  getPressurePumpUnitLabel,
  unitLabels
} from 'hooks/useDisplayUnits/useDisplayUnits';
import { useContext } from 'react';
import {
  BackfillRecipeDto,
  CustomUnitFields,
  DisplayUnits,
  FillType,
  MetricPressureUnit,
  MixerCoefficientsDto,
  OptimisationTargetType,
  PumpType,
  ThroughputControlType,
  UcsCoefficientsDto,
  UnitSystem
} from 'providers/api';
import ProjectContext from 'modules/project/providers/project/context';
import WarningTooltipIcon from '../WarningTooltipIcon';
import RecipeHydraulicFillDetails from './RecipeHydraulicFillDetails';
import RecipePasteFillDetails from './RecipePasteFillDetails';
import UnitSystemDetail from './UnitSystemDetail';

const PREFIX = 'RecipeDetails';

const classes = {
  table: `${PREFIX}-table`,
};

const Root = styled('div')(() => ({
  [`& .${classes.table}`]: {
    '& .MuiTableCell-root': {
      borderBottom: 'none',
      fontSize: '0.9em',
      padding: 0,
      margin: 0,
      paddingBottom: 10,
    },
  },
}));

interface BoosterPumpNode {
  nodeId: string;
  pressure: number;
}

interface RecipeDetailsProps {
  recipe: BackfillRecipeDto;
  displayUnitPreferences: DisplayUnits[];
  customUnitFields: CustomUnitFields[];
  unitSystemPreference: UnitSystem;
  specificGravity?: number;
  mixerCoefficients?: MixerCoefficientsDto;
  heightOfCylinder?: number;
  yieldStressA?: number;
  yieldStressB?: number;
  tailingsSolidsDensity?: number;
  carrierFluidDensity?: number;
  binderParticleDensity?: number;
  fillType: FillType;
  throughputControlType: ThroughputControlType;
  targetDaysCoefficients: { [key: string]: UcsCoefficientsDto; };
  backFillPlantNodeId: string | undefined;
  pumpType: PumpType;
  boosterPumpNodes?: BoosterPumpNode[];
}

const RecipeDetails = ({
  recipe,
  displayUnitPreferences,
  customUnitFields,
  unitSystemPreference,
  specificGravity,
  mixerCoefficients,
  heightOfCylinder,
  yieldStressA,
  yieldStressB,
  tailingsSolidsDensity,
  carrierFluidDensity,
  binderParticleDensity,
  fillType,
  throughputControlType,
  targetDaysCoefficients,
  backFillPlantNodeId,
  pumpType,
  boosterPumpNodes,
}: RecipeDetailsProps) => {
  const projectContext = useContext(ProjectContext);
  const metricPressureUnit = projectContext?.state.metricPressureUnit ?? MetricPressureUnit.Bar;
  const isKiloPascal = metricPressureUnit === MetricPressureUnit.KiloPascal;
  const displayUnitResults = useDisplayUnits([{
    key: 'adjusted',
    displayUnitPreferences,
    specification: fillType === FillType.Paste ? recipe.pasteSpecification : recipe.hydraulicSpecification,
    selectedSpecification: fillType === FillType.Paste ? recipe.pasteSelectedSpecification : recipe.hydraulicSelectedSpecification,
    specificGravity,
    yieldStressA,
    yieldStressB,
    mixerCoefficients,
    heightOfCylinder,
    fillType,
    throughputControlType,
    tailingsSolidsDensity,
    carrierFluidDensity,
    binderParticleDensity,
  },
  {
    key: 'optimised',
    displayUnitPreferences,
    specification: fillType === FillType.Paste ? recipe.pasteSpecification : recipe.hydraulicSpecification,
    selectedSpecification: fillType === FillType.Paste ? recipe.pasteOptimisedSpecification : recipe.hydraulicOptimisedSpecification,
    specificGravity,
    yieldStressA,
    yieldStressB,
    mixerCoefficients,
    heightOfCylinder,
    fillType,
    throughputControlType,
    tailingsSolidsDensity,
    carrierFluidDensity,
    binderParticleDensity,
  }]);

  const { additionalDisplayUnits: additionalAdjustedDisplayUnitMap, warnings } = displayUnitResults.adjusted;
  const { additionalDisplayUnits: additionalOptimisedDisplayUnitMap } = displayUnitResults.optimised;

  const processedWarnings = Object.entries(warnings).filter(([/* key */, value]) => value !== undefined).map(([/* key */, value]) => ({ display: value! }));

  return (
    <Root>
      {processedWarnings && processedWarnings.length > 0 && (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'row-reverse',
            mb: 1,
          }}
        >
          <WarningTooltipIcon warnings={processedWarnings as any} />
        </Box>
      )}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
        }}
      >
        <Typography variant="caption"><Trans id="Rheology Data: ">Rheology Data: </Trans></Typography>
      </Box>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          mb: 1,
        }}
      >
        <Typography variant="caption">{`${recipe.rheologyDataSetReference} - ${recipe.rheologyDataSetVersion}`}</Typography>
      </Box>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
        }}
      >
        <Typography variant="caption"><Trans id="Backfill Plant:">Backfill Plant: </Trans></Typography>
      </Box>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          mb: 1,
        }}
      >
        <Typography variant="caption">
          {`Node ${backFillPlantNodeId || ''}`}
        </Typography>
      </Box>
      <Table className={classes.table} aria-label={i18n._('Recipe details table')} size="small">
        <TableHead>
          <TableRow>
            <TableCell />
            <TableCell align="right"><Trans id="Design">Design</Trans></TableCell>
            <TableCell align="right"><Trans id="Selected">Selected</Trans></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {
            fillType === FillType.Paste
              ? (
                <RecipePasteFillDetails
                  recipe={recipe}
                  unitSystemPreference={unitSystemPreference}
                  throughputControlType={throughputControlType}
                  targetDaysCoefficients={targetDaysCoefficients}
                  customUnitFields={customUnitFields}
                  pumpType={pumpType}
                  boosterPumpNodes={boosterPumpNodes}
                />
              )
              : (
                <RecipeHydraulicFillDetails
                  recipe={recipe}
                  unitSystemPreference={unitSystemPreference}
                  throughputControlType={throughputControlType}
                  customUnitFields={customUnitFields}
                />
              )
          }
          <TableRow>
            <TableCell colSpan={3}><Divider /></TableCell>
          </TableRow>

          {
            Object.entries(additionalAdjustedDisplayUnitMap)
              .filter(([/* key */, unitInfo]) => unitInfo.value !== undefined)
              .map(([key, unitInfo]) => (
                <UnitSystemDetail
                  key={key}
                  designed={(additionalOptimisedDisplayUnitMap as any)[key].value}
                  selected={unitInfo.value}
                  unitSystem={unitSystemPreference}
                  unitInfo={(additionalUnitLabels as any)[key]}
                  decimalPlaces={unitInfo.decimalPlaces}
                  customUnitFields={customUnitFields}
                />
              ))
          }
        </TableBody>
      </Table>
      <Divider />
      {recipe.targetLines && recipe.targetLines.length > 0 && (() => {
        const pressureUnitInfo = pumpType === PumpType.Pump
          ? getPressurePumpUnitLabel(metricPressureUnit)
          : unitLabels[BasicDisplayUnits.Gravity];
        const pressureAbbr = unitSystemPreference === UnitSystem.Imperial
          ? pressureUnitInfo.imperial.abbreviation
          : pressureUnitInfo.metric.abbreviation;
        const pressureDecimalPlaces = pumpType === PumpType.Pump && isKiloPascal ? 0 : 1;
        const toDisplayPressure = (value: number) => {
          if (pumpType !== PumpType.Pump) {
            return pumpPressureBarToGravityMetricHead(value);
          }
          return unitSystemPreference === UnitSystem.Metric && isKiloPascal ? pumpPressureBarToKpa(value) : value;
        };

        const isImperial = unitSystemPreference === UnitSystem.Imperial;
        const flowRateUnitInfo = additionalUnitLabels[DisplayUnits.FlowRate];
        const flowRateAbbr = isImperial ? flowRateUnitInfo.imperial.abbreviation : flowRateUnitInfo.metric.abbreviation;
        const frictionLossUnitInfo = additionalUnitLabels[DisplayUnits.FrictionLoss];
        const frictionLossAbbr = isImperial ? frictionLossUnitInfo.imperial.abbreviation : frictionLossUnitInfo.metric.abbreviation;
        const metricTargetPressureAbbr = isKiloPascal ? i18n._('kPa') : unitLabels[BasicDisplayUnits.PressurePump].metric.abbreviation;
        const targetPressureAbbr = isImperial ? unitLabels[BasicDisplayUnits.PressurePump].imperial.abbreviation : metricTargetPressureAbbr;

        const targetTypeLabels: Record<OptimisationTargetType, string> = {
          [OptimisationTargetType.KPaPerM]: 'Friction Loss',
          [OptimisationTargetType.PumpPressure]: 'Pump Pressure',
          [OptimisationTargetType.Yield]: 'Yield',
          [OptimisationTargetType.FlowRate]: 'Flow Rate',
        };

        // The target's own configured value, so two flow rate targets on one recipe are tellable
        // apart. Values are stored canonically (kPa/m, bar, m³/h) and converted for display only,
        // matching the inputs in TargetLinesSection — deliberately NOT toDisplayPressure, which
        // applies the gravity-head conversion used for the achieved result below.
        const describeTargetValue = (tl: NonNullable<BackfillRecipeDto['targetLines']>[number]): string => {
          if (tl.targetType === OptimisationTargetType.KPaPerM && tl.kPaPerMTarget !== undefined) {
            const value = isImperial ? round(frictionLossMetricToImperial(tl.kPaPerMTarget), 3) : tl.kPaPerMTarget;
            return `${value} ${frictionLossAbbr}`;
          }
          if (tl.targetType === OptimisationTargetType.PumpPressure && tl.pumpPressureTarget !== undefined) {
            const metricValue = isKiloPascal ? pumpPressureBarToKpa(tl.pumpPressureTarget) : tl.pumpPressureTarget;
            const value = isImperial ? round(pumpPressureMetricToImperial(tl.pumpPressureTarget), 1) : metricValue;
            return `${value} ${targetPressureAbbr}`;
          }
          if (tl.targetType === OptimisationTargetType.Yield && tl.yieldTarget !== undefined) {
            return `${tl.yieldTarget} ${i18n._('Pa')}`;
          }
          if (tl.targetType === OptimisationTargetType.FlowRate && tl.flowRateTarget !== undefined) {
            const value = isImperial ? round(flowRateMetricToImperial(tl.flowRateTarget), 1) : tl.flowRateTarget;
            return `${value} ${flowRateAbbr}`;
          }
          return '';
        };

        return (
          <>
            <Box sx={{ mt: 2, mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                <Trans id="Target Line Results">Target Line Results</Trans>
              </Typography>
            </Box>
            {recipe.targetLines!.map((tl) => {
              const key = `${tl.targetType}-${tl.targetPipeId ?? ''}-${tl.kPaPerMTarget ?? ''}-${tl.pumpPressureTarget ?? ''}`
                + `-${tl.yieldTarget ?? ''}-${tl.flowRateTarget ?? ''}`;
              const typeLabel = tl.targetType !== undefined ? targetTypeLabels[tl.targetType] ?? 'Unknown' : 'Unknown';
              const valueLabel = tl.targetType !== undefined ? describeTargetValue(tl) : '';
              return (
                <Box key={key} sx={{ mb: 1, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    {valueLabel ? `${typeLabel} — ${valueLabel}` : typeLabel}
                  </Typography>
                  {tl.result ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      <Chip size="small" label={`Mass Conc: ${round(tl.result.massConcentration, 2)}%`} />
                      <Chip size="small" label={`Binder: ${round(tl.result.binderContent, 2)}%`} />
                      <Chip size="small" label={`Pressure: ${round(toDisplayPressure(tl.result.pumpPressure), pressureDecimalPlaces)} ${pressureAbbr}`} />
                      <Chip size="small" label={`UCS: ${round(tl.result.ucsStrength, 0)} ${i18n._('kPa')}`} />
                      {tl.hasErrors && tl.errorDescription && (
                        <Chip size="small" color="warning" label={tl.errorDescription} />
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="error">
                      {tl.errorDescription || i18n._('No solution found')}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </>
        );
      })()}
    </Root>
  );
};

export default RecipeDetails;
